import type { DataTableApi } from '@components/DataTable';
import { Switch } from '@components/Switch/Switch';
import { SHORTCUTS } from '@lib/keyboard/shortcuts';
import { useKeyboardShortcut } from '@lib/keyboard/useKeyboardShortcut';
import { countOmniTokens } from '@lib/utils/omniFilter';
import { RefObject } from 'react';
import css from './OmniFilter.module.scss';

/**
 * Where a search line is the omnifilter, the Filters pill steps aside. These are the pill's two
 * other jobs, moved to that line: the match mode and ⌘F / Ctrl+F.
 */

/** "Match all terms", beside the search. Only once there are two terms for it to join. */
export const OmniMatchAllToggle = ({
  query,
  matchAll,
  onMatchAllChange,
}: {
  query: string;
  matchAll: boolean;
  onMatchAllChange: (matchAll: boolean) => void;
}) => {
  if (countOmniTokens(query) < 2) return null;
  return (
    <div className={css.tableMatchMode}>
      <span className={css.matchModeLabel} onClick={() => onMatchAllChange(!matchAll)}>
        Match all terms
      </span>
      <Switch checked={matchAll} onChange={onMatchAllChange} />
    </div>
  );
};

/**
 * ⌘F / Ctrl+F focuses a search line that stands in for the Filters pill. Where the pill is still
 * mounted, it holds the shortcut instead, so the two never both do. With no search to focus (it
 * returned null), the browser's own find runs.
 */
export function useFindShortcut(
  search: () => { focus: () => void } | null,
  onOpen: (via: 'keyboard') => void,
) {
  useKeyboardShortcut({
    shortcut: SHORTCUTS.openFilters,
    onMatch: (event) => {
      const target = search();
      if (!target) return;
      event.preventDefault();
      if (event.repeat) return;
      target.focus();
      onOpen('keyboard');
    },
  });
}

/** {@link useFindShortcut} for a table's own search line. */
export function useTableFindShortcut(
  apiRef: RefObject<DataTableApi | null>,
  onOpen: (via: 'keyboard') => void,
) {
  useFindShortcut(() => (apiRef.current ? { focus: apiRef.current.focusSearch } : null), onOpen);
}
